import { useState, useMemo } from "react";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from "date-fns";
import { ru } from "date-fns/locale";
import { CalendarBlank, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Date presets similar to the reference design
const getPresets = () => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  
  return [
    { 
      label: "Сегодня", 
      from: startOfToday, 
      to: today 
    },
    { 
      label: "Вчера", 
      from: subDays(startOfToday, 1), 
      to: subDays(today, 1) 
    },
    { 
      label: "На этой неделе", 
      from: startOfWeek(today, { weekStartsOn: 1 }), 
      to: today 
    },
    { 
      label: "На прошлой неделе", 
      from: startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }), 
      to: endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }) 
    },
    { 
      label: "В этом месяце", 
      from: startOfMonth(today), 
      to: today 
    },
    { 
      label: "В прошлом месяце", 
      from: startOfMonth(subMonths(today, 1)), 
      to: endOfMonth(subMonths(today, 1)) 
    },
  ];
};

export default function DateRangePicker({ 
  dateRange, 
  onDateRangeChange,
  className 
}) {
  const [open, setOpen] = useState(false);
  const [customDays, setCustomDays] = useState(30);
  
  const presets = useMemo(() => getPresets(), []);
  
  // Handle preset selection
  const handlePresetClick = (preset) => {
    onDateRangeChange({ from: preset.from, to: preset.to });
    setOpen(false);
  };
  
  // Handle custom days selection
  const handleCustomDays = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const from = subDays(today, customDays);
    from.setHours(0, 0, 0, 0);
    onDateRangeChange({ from, to: today });
    setOpen(false);
  };
  
  // Format date for display
  const formatDateShort = (date) => {
    if (!date) return "";
    return format(date, "d MMM yyyy", { locale: ru });
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal gap-2 bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700 hover:text-white",
            className
          )}
        >
          <CalendarBlank size={16} className="text-zinc-400" />
          {dateRange?.from && dateRange?.to ? (
            <span>
              {formatDateShort(dateRange.from)} — {formatDateShort(dateRange.to)}
            </span>
          ) : (
            <span className="text-zinc-400">Выберите период</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-auto p-0 bg-zinc-900 border-zinc-700" 
        align="start"
        sideOffset={8}
      >
        <div className="flex">
          {/* Left panel - Presets */}
          <div className="border-r border-zinc-700 p-3 space-y-1 min-w-[180px]">
            {presets.map((preset, idx) => (
              <button
                key={idx}
                onClick={() => handlePresetClick(preset)}
                className="w-full text-left px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-md transition-colors"
              >
                {preset.label}
              </button>
            ))}
            
            {/* Custom days input */}
            <div className="pt-3 border-t border-zinc-700 mt-3">
              <div className="flex items-center gap-2 px-3">
                <input
                  type="number"
                  value={customDays}
                  onChange={(e) => setCustomDays(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-14 px-2 py-1.5 text-sm bg-zinc-800 border border-zinc-600 rounded text-white text-center focus:outline-none focus:border-blue-500"
                  min="1"
                  max="365"
                />
                <button
                  onClick={handleCustomDays}
                  className="flex-1 text-left text-sm text-zinc-300 hover:text-white transition-colors"
                >
                  дней до сегодня
                </button>
              </div>
            </div>
          </div>
          
          {/* Right panel - Calendar */}
          <div className="p-3">
            {/* Selected range display */}
            <div className="flex items-center gap-2 mb-3 px-2">
              <div className="flex-1 px-3 py-2 bg-zinc-800 rounded-md text-center text-sm text-white">
                {formatDateShort(dateRange?.from)}
              </div>
              <span className="text-zinc-500">—</span>
              <div className="flex-1 px-3 py-2 bg-zinc-800 rounded-md text-center text-sm text-white">
                {formatDateShort(dateRange?.to)}
              </div>
            </div>
            
            {/* Two-month calendar */}
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={(range) => {
                if (range?.from && range?.to) {
                  onDateRangeChange(range);
                } else if (range?.from) {
                  onDateRangeChange({ from: range.from, to: range.from });
                }
              }}
              numberOfMonths={2}
              locale={ru}
              weekStartsOn={1}
              className="text-white"
              classNames={{
                months: "flex gap-4",
                month: "space-y-4",
                caption: "flex justify-center pt-1 relative items-center text-zinc-300",
                caption_label: "text-sm font-medium",
                nav: "space-x-1 flex items-center",
                nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-zinc-300 hover:text-white",
                nav_button_previous: "absolute left-1",
                nav_button_next: "absolute right-1",
                table: "w-full border-collapse space-y-1",
                head_row: "flex",
                head_cell: "text-zinc-500 rounded-md w-9 font-normal text-[0.8rem]",
                row: "flex w-full mt-2",
                cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-blue-500/20 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 text-zinc-300 hover:bg-zinc-700 hover:text-white rounded-md",
                day_selected: "bg-blue-600 text-white hover:bg-blue-700 hover:text-white focus:bg-blue-600 focus:text-white",
                day_today: "bg-zinc-700 text-white",
                day_outside: "text-zinc-600 opacity-50",
                day_disabled: "text-zinc-600 opacity-50",
                day_range_middle: "aria-selected:bg-blue-500/30 aria-selected:text-white",
                day_hidden: "invisible",
              }}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
